
import React, { createContext, useContext, ReactNode, useState, useEffect, useRef } from 'react';
import { I18nextProvider } from 'react-i18next';
import { usePublicCommunity } from '@/contexts/PublicCommunityContext';
import i18n, { initializeI18n } from '@/i18n/i18n';
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

interface TranslationContextType {
  locale: string;
  setLocale: (locale: string) => void;
  availableLocales: string[];
  isInitialized: boolean;
}

const TranslationContext = createContext<TranslationContextType | null>(null);

export const useTranslationContext = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslationContext must be used within a TranslationProvider');
  }
  return context;
};

export const TranslationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentCommunity } = usePublicCommunity();
  const [isI18nInitialized, setIsI18nInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [currentLocale, setCurrentLocale] = useState('en_GB');
  const previousCommunityIdRef = useRef<string | null>(null);
  
  // Initialize i18n when community data is available or changes
  useEffect(() => {
    const initTranslations = async () => {
      if (!currentCommunity?.id) {
        console.log('[TranslationProvider] Waiting for community data...');
        return;
      }

      // If community ID changed, we need to reinitialize i18n
      if (previousCommunityIdRef.current !== currentCommunity.id) {
        console.log('[TranslationProvider] Community changed, reinitializing i18n');
        setIsI18nInitialized(false);
        previousCommunityIdRef.current = currentCommunity.id;
      } else if (isI18nInitialized) {
        console.log('[TranslationProvider] i18n already initialized for this community');
        return;
      }

      try {
        console.log('[TranslationProvider] Initializing i18n with community:', currentCommunity.id);
        await initializeI18n(currentCommunity.id);
        console.log('[TranslationProvider] i18n initialized successfully');
        setIsI18nInitialized(true);
        
        // Set initial locale from community settings
        if (currentCommunity.default_locale) {
          console.log(`[TranslationProvider] Setting initial locale to: ${currentCommunity.default_locale}`);
          i18n.changeLanguage(currentCommunity.default_locale);
          setCurrentLocale(currentCommunity.default_locale);
        }
      } catch (error) {
        console.error('[TranslationProvider] Failed to initialize i18n:', error);
        setInitError(error instanceof Error ? error.message : 'Failed to initialize translations');
        setIsI18nInitialized(false);
      }
    };

    initTranslations();
  }, [currentCommunity]);

  const setLocale = (newLocale: string) => {
    if (isI18nInitialized) {
      console.log(`[TranslationProvider] Changing locale to: ${newLocale}`);
      i18n.changeLanguage(newLocale);
      setCurrentLocale(newLocale);
    } else {
      console.warn('[TranslationProvider] Cannot change language, i18n not initialized yet');
    }
  };

  // Show error state if initialization failed
  if (initError) {
    console.error('[TranslationProvider] Translation initialization error:', initError);
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertTitle>Translation Error</AlertTitle>
          <AlertDescription>{initError}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Don't show a loading spinner here, as PublicCommunityContext already handles that
  // Only render the I18nextProvider when initialized or pass null for children to prevent rendering before initialization
  return (
    <I18nextProvider i18n={i18n}>
      <TranslationContext.Provider 
        value={{
          locale: currentLocale,
          setLocale,
          availableLocales: currentCommunity?.available_locales || ['en_GB'],
          isInitialized: isI18nInitialized
        }}
      >
        {isI18nInitialized ? children : null}
      </TranslationContext.Provider>
    </I18nextProvider>
  );
};
